import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cn } from '@zond/utils';
import { X } from 'lucide-react';
import { AnimatePresence, motion, type Transition } from 'motion/react';
import * as React from 'react';
import { buttonVariants } from './button';

interface AlertDialogContextType {
  isOpen: boolean;
}
const AlertDialogContext = React.createContext<AlertDialogContextType>({ isOpen: false });

const useAlertDialog = (): AlertDialogContextType => {
  const context = React.useContext(AlertDialogContext);
  if (!context) {
    throw new Error('useAlertDialog must be used within an AlertDialog');
  }
  return context;
};

type AlertDialogProps = React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Root>;
const AlertDialog: React.FC<AlertDialogProps> = ({ children, ...props }) => {
  const [isOpen, setIsOpen] = React.useState(props?.open ?? props?.defaultOpen ?? false);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      props.onOpenChange?.(open);
    },
    [props]
  );

  // Synchronization with the external open prop when it changes
  React.useEffect(() => {
    if (props.open !== undefined && props.open !== isOpen) {
      setIsOpen(props.open);
    }
  }, [props.open, isOpen]);

  const contextValue = React.useMemo(
    () => ({
      isOpen: props.open !== undefined ? props.open : isOpen,
    }),
    [props.open, isOpen]
  );

  return (
    <AlertDialogPrimitive.Root {...props} onOpenChange={handleOpenChange}>
      <AlertDialogContext.Provider value={contextValue}>{children}</AlertDialogContext.Provider>
    </AlertDialogPrimitive.Root>
  );
};

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

type FlipDirection = 'top' | 'bottom' | 'left' | 'right';

type AlertDialogContentProps = React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & {
  from?: FlipDirection;
  transition?: Transition;
};

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(
  (
    {
      className,
      children,
      from = 'top',
      transition = { type: 'spring', stiffness: 150, damping: 25 },
      ...props
    },
    ref
  ) => {
    const { isOpen } = useAlertDialog();

    const initialRotation = from === 'top' || from === 'left' ? '20deg' : '-20deg';
    const isVertical = from === 'top' || from === 'bottom';
    const rotateAxis = isVertical ? 'rotateX' : 'rotateY';

    // Unique ID for description
    const descriptionId = React.useId();

    return (
      <AnimatePresence>
        {isOpen && (
          <AlertDialogPortal forceMount>
            <AlertDialogPrimitive.Overlay asChild forceMount>
              <motion.div
                key="alert-dialog-overlay"
                initial={{ opacity: 0, filter: 'blur(4px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(4px)' }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="fixed inset-0 z-50 bg-black/80"
              />
            </AlertDialogPrimitive.Overlay>
            <AlertDialogPrimitive.Content
              asChild
              forceMount
              ref={ref}
              aria-describedby={descriptionId}
              {...props}
            >
              <motion.div
                key="alert-dialog-content"
                initial={{
                  opacity: 0,
                  filter: 'blur(4px)',
                  transform: `translate(-50%, -50%) perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`,
                }}
                animate={{
                  opacity: 1,
                  filter: 'blur(0px)',
                  transform: `translate(-50%, -50%) perspective(500px) ${rotateAxis}(0deg) scale(1)`,
                }}
                exit={{
                  opacity: 0,
                  filter: 'blur(4px)',
                  transform: `translate(-50%, -50%) perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`,
                }}
                transition={transition}
                className={cn(
                  'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg sm:rounded-lg',
                  className
                )}
              >
                {children}
                <AlertDialogPrimitive.Description id={descriptionId} className="sr-only">
                  This alert dialog requires your attention and confirmation
                </AlertDialogPrimitive.Description>
              </motion.div>
            </AlertDialogPrimitive.Content>
          </AlertDialogPortal>
        )}
      </AnimatePresence>
    );
  }
);
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

const AlertDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title ref={ref} className={cn('text-lg font-semibold', className)} {...props} />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action ref={ref} className={cn(buttonVariants(), className)} {...props} />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonVariants({ variant: 'outline' }), 'mt-2 sm:mt-0', className)}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  useAlertDialog,
  type AlertDialogContextType,
  type AlertDialogContentProps,
};
